/**
 * @engine/vfx - Texture Loading Utilities
 * 
 * Utilities for loading textures from image files (PNG, JPG, etc.)
 * for use with VFX renderers.
 */

import type { IRenderContext, TextureHandle } from '@engine/render2d';
import { embeddedAssets } from '@engine/core';

/**
 * Options for loading a texture.
 */
export interface LoadTextureOptions {
  /** Label for the texture (for debugging) */
  label?: string;
  /** Generate mipmaps (default: false for VFX) */
  generateMipmaps?: boolean;
}

/**
 * Load a texture from a URL (PNG, JPG, WebP, etc.)
 * 
 * @param ctx - Render context
 * @param url - URL to the image file
 * @param options - Loading options
 * @returns Promise resolving to TextureHandle
 * 
 * @example
 * ```typescript
 * const texture = await loadTextureFromURL(ctx, '/assets/default_texture.png');
 * renderer.setDefaultTexture(texture);
 * ```
 */
export async function loadTextureFromURL(
  ctx: IRenderContext,
  url: string,
  options: LoadTextureOptions = {}
): Promise<TextureHandle> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch texture: ${url} (${response.status})`);
  }
  
  const blob = await response.blob();
  return loadTextureFromBlob(ctx, blob, options);
}

/**
 * Load a texture from a Blob.
 * 
 * @param ctx - Render context
 * @param blob - Image blob
 * @param options - Loading options
 * @returns Promise resolving to TextureHandle
 */
export async function loadTextureFromBlob(
  ctx: IRenderContext,
  blob: Blob,
  options: LoadTextureOptions = {}
): Promise<TextureHandle> {
  // Use createImageBitmap for efficient decoding
  const bitmap = await createImageBitmap(blob, {
    premultiplyAlpha: 'premultiply',
    colorSpaceConversion: 'none',
  });
  
  return loadTextureFromImageBitmap(ctx, bitmap, options);
}

/**
 * Load a texture from an ImageBitmap.
 * 
 * @param ctx - Render context
 * @param bitmap - ImageBitmap
 * @param options - Loading options
 * @returns TextureHandle
 */
export function loadTextureFromImageBitmap(
  ctx: IRenderContext,
  bitmap: ImageBitmap,
  options: LoadTextureOptions = {}
): TextureHandle {
  const { label = 'loaded-texture', generateMipmaps = false } = options;
  
  // Calculate mip levels if mipmaps requested
  const mipLevelCount = generateMipmaps 
    ? Math.floor(Math.log2(Math.max(bitmap.width, bitmap.height))) + 1
    : 1;
  
  // Create texture
  const texture = ctx.createTexture({
    width: bitmap.width,
    height: bitmap.height,
    format: 'rgba8unorm',
    usage: ['sampled', 'copy-dst', 'render-target'],
    mipmaps: generateMipmaps,
    label,
  });
  
  // Copy image data to texture
  // Note: IRenderContext should have updateTextureFromImageBitmap or similar
  // For now, we use a canvas fallback if the method doesn't exist
  if ('updateTextureFromImageBitmap' in ctx && typeof ctx.updateTextureFromImageBitmap === 'function') {
    (ctx as { updateTextureFromImageBitmap: (tex: TextureHandle, bitmap: ImageBitmap) => void })
      .updateTextureFromImageBitmap(texture, bitmap);
  } else {
    // Fallback: draw to canvas and read pixels
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx2d = canvas.getContext('2d')!;
    ctx2d.drawImage(bitmap, 0, 0);
    const imageData = ctx2d.getImageData(0, 0, bitmap.width, bitmap.height);
    ctx.updateTexture(texture, new Uint8Array(imageData.data.buffer));
  }
  
  bitmap.close();
  
  return texture;
}

/**
 * Path to the default VFX texture (white particle).
 * Uses embeddedAssets for dev/prod path resolution.
 */
export const DEFAULT_TEXTURE_PATH = embeddedAssets.resolve('vfx-default-texture');

/**
 * Load the default VFX texture.
 * This is a convenience function that loads default_texture.png bundled with the package.
 * 
 * @param ctx - Render context
 * @returns Promise resolving to TextureHandle
 * 
 * @example
 * ```typescript
 * const defaultTex = await loadDefaultVFXTexture(ctx);
 * renderer.setDefaultTexture(defaultTex);
 * ```
 */
export async function loadDefaultVFXTexture(ctx: IRenderContext): Promise<TextureHandle> {
  return loadTextureFromURL(ctx, DEFAULT_TEXTURE_PATH, {
    label: 'vfx-default-texture',
  });
}
